import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { fetchData } from "../../api/client";
import { toE164Digits } from "../../utils/phone";

const CARD_MAX_W = 280;

function initialsFromName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function onlyDigits(s) {
  return String(s || "").replace(/\D/g, "");
}

function buildCreateClientPayload(contact, projectId) {
  const pid = Number(projectId);
  if (!Number.isFinite(pid) || pid <= 0) return null;
  const nome = String(contact?.displayName || "").trim();
  const rawWa = contact?.whatsappDigits || contact?.whatsappE164;
  const rawPhone = contact?.phoneDisplay || contact?.phoneRaw;
  const dWa = onlyDigits(rawWa);
  const dPhone = onlyDigits(rawPhone);
  const digits = dWa || dPhone;
  if (!nome || digits.length < 8) return null;
  const e164Wa = toE164Digits(rawWa || digits, "BR") || digits;
  const e164Tel = rawPhone ? toE164Digits(rawPhone, "BR") : e164Wa;
  const payload = {
    id_projeto: pid,
    nome,
    whatsapp: e164Wa,
    celular: e164Wa,
    telefone: e164Tel || null,
  };
  const em = contact?.email != null ? String(contact.email).trim() : "";
  if (em) payload.email = em;
  return payload;
}

function pickErrorMessage(err) {
  if (!err) return "";
  if (typeof err.message === "string" && err.message.trim()) return err.message.trim();
  const inner = err.data || err.response?.data;
  if (inner && typeof inner.message === "string" && inner.message.trim()) return inner.message.trim();
  return "";
}

/**
 * @param {{
 *   contact: { displayName: string, phoneDisplay: string, whatsappDigits: string, email: string|null },
 *   presentation?: "whatsapp" | "default",
 *   conversationProjectId?: number | string | null,
 * }} props
 */
const ContactCardMessage = ({
  contact,
  presentation = "default",
  conversationProjectId = null,
}) => {
  const { t } = useTranslation("chat");
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
    clientId: null,
  });

  const createPayload = useMemo(
    () => buildCreateClientPayload(contact, conversationProjectId),
    [contact, conversationProjectId]
  );

  const projectNum = Number(conversationProjectId);
  const hasProject = Number.isFinite(projectNum) && projectNum > 0;

  const handleSave = useCallback(async () => {
    if (!createPayload || saving) return;
    setSaving(true);
    try {
      const res = await fetchData("/clients", "POST", createPayload);
      const raw = res?.data;
      const newId =
        raw && typeof raw === "object" && raw.id != null
          ? Number(raw.id)
          : raw != null && Number.isFinite(Number(raw))
          ? Number(raw)
          : null;
      queryClient.invalidateQueries("clients");
      setSnackbar({
        open: true,
        message: t("sharedContact.saveSuccess"),
        severity: "success",
        clientId: Number.isFinite(newId) ? newId : null,
      });
    } catch (err) {
      const msg = pickErrorMessage(err);
      const isDup =
        err?.code === "errors.clients.duplicate" ||
        err?.data?.code === "errors.clients.duplicate";
      setSnackbar({
        open: true,
        message: isDup ? msg || t("sharedContact.saveDuplicate") : msg || t("sharedContact.saveError"),
        severity: "error",
        clientId: null,
      });
    } finally {
      setSaving(false);
    }
  }, [createPayload, saving, queryClient, t]);

  if (!contact || typeof contact !== "object") return null;

  const { displayName, phoneDisplay, whatsappDigits, email } = contact;
  const isWa = presentation === "whatsapp";
  const cardBg = theme.palette.mode === "dark" ? "rgba(18,18,18,0.98)" : "#ffffff";
  const titleColor =
    theme.palette.mode === "dark" ? theme.palette.grey[100] : theme.palette.grey[900];
  const subColor =
    theme.palette.mode === "dark" ? theme.palette.grey[400] : theme.palette.grey[700];
  const linkColor = theme.palette.mode === "dark" ? "#53bdeb" : "#039be5";
  const avatarBg =
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)";

  const copyPhone = () => {
    const raw = phoneDisplay || (whatsappDigits ? `+${onlyDigits(whatsappDigits)}` : "");
    if (!raw || !navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(raw).catch(() => {});
  };

  const canSave = Boolean(createPayload) && hasProject && !saving;
  const saveDisabledReason = !hasProject
    ? t("sharedContact.needProject")
    : !createPayload
    ? t("sharedContact.invalidContact")
    : "";

  const saveButton = (
    <Button
      type="button"
      size="small"
      onClick={handleSave}
      disabled={!canSave}
      sx={{
        alignSelf: "flex-start",
        textTransform: "none",
        fontWeight: isWa ? 600 : 500,
        color: linkColor,
        minWidth: 0,
        px: 0,
        justifyContent: "flex-start",
      }}
    >
      {saving ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={14} sx={{ color: linkColor }} />
          {t("sharedContact.saving")}
        </Box>
      ) : (
        t("sharedContact.saveToClients")
      )}
    </Button>
  );

  return (
    <>
      <Box
        sx={{
          maxWidth: CARD_MAX_W,
          borderRadius: "8px",
          overflow: "hidden",
          border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
          bgcolor: cardBg,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.25, px: 1.5, py: 1.25, alignItems: "center" }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              bgcolor: avatarBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              color: titleColor,
              fontWeight: 700,
              fontSize: "1rem",
            }}
            aria-hidden
          >
            {initialsFromName(displayName) === "?" ? (
              <PersonRoundedIcon sx={{ opacity: 0.85 }} />
            ) : (
              initialsFromName(displayName)
            )}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: titleColor,
                lineHeight: 1.35,
                wordBreak: "break-word",
              }}
            >
              {displayName}
            </Typography>
            {phoneDisplay ? (
              <Typography
                variant="caption"
                component="div"
                sx={{ mt: 0.35, color: subColor, lineHeight: 1.35, wordBreak: "break-word" }}
              >
                {phoneDisplay}
              </Typography>
            ) : null}
            {email ? (
              <Typography
                variant="caption"
                component="div"
                sx={{ mt: 0.25, color: subColor, lineHeight: 1.35, wordBreak: "break-word" }}
              >
                {email}
              </Typography>
            ) : null}
          </Box>
        </Box>
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
          }}
        >
          {saveDisabledReason && !canSave && !saving ? (
            <Tooltip title={saveDisabledReason}>
              <span>{saveButton}</span>
            </Tooltip>
          ) : (
            saveButton
          )}
          {(phoneDisplay || whatsappDigits) && (
            <Button
              type="button"
              size="small"
              onClick={copyPhone}
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                fontWeight: 500,
                color: subColor,
                minWidth: 0,
                px: 0,
                justifyContent: "flex-start",
              }}
            >
              {t("sharedContact.copyPhone")}
            </Button>
          )}
          <Typography
            variant="caption"
            sx={{
              color: subColor,
              opacity: 0.92,
              fontWeight: isWa ? 500 : 400,
            }}
          >
            {t("sharedContact.footer")}
          </Typography>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "success" ? 8000 : 6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
          action={
            snackbar.severity === "success" && snackbar.clientId != null ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  navigate(`/clients/${snackbar.clientId}`);
                  setSnackbar((s) => ({ ...s, open: false }));
                }}
              >
                {t("sharedContact.openClient")}
              </Button>
            ) : undefined
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactCardMessage;
